import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  DocumentScanner as ScannerIcon,
  ReceiptLong as InvoiceIcon,
  Receipt as ReceiptIcon,
  AutoFixHigh as ExtractIcon,
  CheckCircle as CheckIcon,
  WarningAmber as WarnIcon,
} from '@mui/icons-material';
import { apiClient } from '../api/client';

type Mode = 'invoice' | 'receipt';

interface LineItem {
  description?: string;
  qty?: number | null;
  unit_price?: number | null;
}

interface Extracted {
  [key: string]: any;
  line_items?: LineItem[];
  _totals_consistent?: boolean;
  _money_corrections?: string[];
}

// Fields surfaced (and made editable) per document type.
const FIELDS: Record<Mode, { key: string; label: string }[]> = {
  invoice: [
    { key: 'supplier', label: 'Supplier' },
    { key: 'invoice_number', label: 'Invoice #' },
    { key: 'invoice_date', label: 'Date' },
    { key: 'payment_terms', label: 'Terms' },
    { key: 'currency', label: 'Currency' },
    { key: 'net_amount', label: 'Net' },
    { key: 'vat_amount', label: 'VAT' },
    { key: 'total', label: 'Total' },
  ],
  receipt: [
    { key: 'merchant', label: 'Merchant' },
    { key: 'date', label: 'Date' },
    { key: 'currency', label: 'Currency' },
    { key: 'total', label: 'Total' },
    { key: 'tax', label: 'Tax' },
    { key: 'category', label: 'Category' },
    { key: 'payment_method', label: 'Payment method' },
  ],
};

const SAMPLE =
  'From: Brightwave Supplies Ltd, 14 Mill Rd, Leeds\nInv #: BW-2026-0091  Dated 3 June 2026, payment within 30 days\nA4 paper boxes  12  £4.50\nToner cartridge HP  3  £62.00\nDelivery  £15.00\nSubtotal £255.00  VAT(20%) £51.00  TOTAL DUE £306.00 GBP';

const DocumentScanner: React.FC = () => {
  const [mode, setMode] = useState<Mode>('invoice');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<Extracted | null>(null);

  const extract = async () => {
    if (!text.trim() || loading) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await apiClient.post(`/extract/${mode}`, { text });
      setData(res.data?.data ?? {});
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ||
          'The extraction service is unavailable. Ensure the EFAI extractor is running (port 8089).'
      );
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key: string, value: string) =>
    setData((prev) => ({ ...(prev || {}), [key]: value }));

  const consistent = data?._totals_consistent;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <ScannerIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          Document Scanner
        </Typography>
        <Chip size="small" label="LFM2.5-230M" sx={{ ml: 1 }} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Paste the text of an invoice or receipt (or OCR output) and extract structured fields to
        pre-fill a bill or expense. Money totals are validated deterministically.
      </Typography>

      <Grid container spacing={2}>
        {/* Input */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <ToggleButtonGroup
              value={mode}
              exclusive
              size="small"
              onChange={(_, v) => v && setMode(v)}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="invoice">
                <InvoiceIcon fontSize="small" sx={{ mr: 0.5 }} /> Invoice
              </ToggleButton>
              <ToggleButton value="receipt">
                <ReceiptIcon fontSize="small" sx={{ mr: 0.5 }} /> Receipt
              </ToggleButton>
            </ToggleButtonGroup>

            <TextField
              fullWidth
              multiline
              minRows={10}
              placeholder="Paste invoice / receipt text here…"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <ExtractIcon />}
                onClick={extract}
                disabled={loading || !text.trim()}
              >
                Extract
              </Button>
              <Button variant="text" onClick={() => setText(SAMPLE)} disabled={loading}>
                Use sample
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Output */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, minHeight: 320 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
              Extracted fields
            </Typography>

            {error && (
              <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {!data && !error && (
              <Typography variant="body2" color="text.secondary">
                Extracted fields will appear here, ready to review and edit.
              </Typography>
            )}

            {data && (
              <>
                {mode === 'invoice' && consistent !== undefined && (
                  <Chip
                    size="small"
                    color={consistent ? 'success' : 'warning'}
                    icon={consistent ? <CheckIcon /> : <WarnIcon />}
                    label={consistent ? 'Totals consistent (net + VAT = total)' : 'Check totals — net + VAT ≠ total'}
                    sx={{ mb: 2 }}
                  />
                )}

                <Grid container spacing={1.5}>
                  {FIELDS[mode].map((f) => (
                    <Grid item xs={6} key={f.key}>
                      <TextField
                        fullWidth
                        size="small"
                        label={f.label}
                        value={data[f.key] ?? ''}
                        onChange={(e) => updateField(f.key, e.target.value)}
                      />
                    </Grid>
                  ))}
                </Grid>

                {mode === 'invoice' && (data.line_items?.length ?? 0) > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Line items
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">Qty</TableCell>
                          <TableCell align="right">Unit price</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.line_items!.map((li, i) => (
                          <TableRow key={i}>
                            <TableCell>{li.description}</TableCell>
                            <TableCell align="right">{li.qty ?? '—'}</TableCell>
                            <TableCell align="right">{li.unit_price ?? '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}

                <Box sx={{ mt: 2 }}>
                  <Button variant="contained" color="success" disabled>
                    Create {mode === 'invoice' ? 'bill' : 'expense'} (review then save)
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Review the fields above, then create the {mode === 'invoice' ? 'bill' : 'expense'}.
                  </Typography>
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DocumentScanner;
